import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildLessonChatStreamUrl, streamGet } from "../../utils/aiSse";
import { toastAiError } from "../../utils/aiErrors";

const LessonChatPanel = ({ lessonId, getPlayedSeconds }) => {
  const { t } = useTranslation("ai");
  const [message, setMessage] = useState("");
  const [turns, setTurns] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [incomplete, setIncomplete] = useState(false);
  const [lastPayload, setLastPayload] = useState(null);
  const abortRef = useRef(null);
  const messagesRef = useRef(null);

  useEffect(() => {
    setTurns([]);
    setMessage("");
    setStreaming(false);
    setIncomplete(false);
    setLastPayload(null);
    abortRef.current?.abort();
    return () => abortRef.current?.abort();
  }, [lessonId]);

  useEffect(() => {
    if (!turns.length) return;
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [turns, streaming]);

  const ask = async (payload) => {
    const text = (payload?.message ?? message).trim();
    if (!text || streaming) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const timestamp =
      typeof getPlayedSeconds === "function" ? getPlayedSeconds() : undefined;

    setLastPayload({ message: text, timestamp });
    setIncomplete(false);
    setMessage("");
    setTurns((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "", pending: true },
    ]);
    setStreaming(true);

    const path = buildLessonChatStreamUrl(lessonId, text, timestamp);

    try {
      await streamGet(path, {
        signal: controller.signal,
        onToken: (token) => {
          setTurns((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === "assistant") {
              next[next.length - 1] = {
                ...last,
                content: (last.content || "") + token,
                pending: true,
              };
            }
            return next;
          });
        },
      });

      setTurns((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          next[next.length - 1] = { ...last, pending: false };
        }
        return next;
      });
      setStreaming(false);
    } catch (err) {
      if (controller.signal.aborted) return;
      setStreaming(false);
      if (err.incomplete) {
        setIncomplete(true);
        setTurns((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === "assistant") {
            next[next.length - 1] = { ...last, pending: false };
          }
          return next;
        });
        return;
      }
      toastAiError(err, t);
      setTurns((prev) => prev.slice(0, -1));
    }
  };

  return (
    <div className="ai-lesson-chat">
      <p className="ai-muted">{t("chat.emptyHint")}</p>

      <div className="ai-chat-messages" ref={messagesRef}>
        {turns.map((turn, i) => (
          <div
            key={`${turn.role}-${i}`}
            className={`ai-chat-bubble ai-chat-bubble--${turn.role}`}
          >
            <span className="ai-chat-bubble__role">
              {turn.role === "user" ? t("chat.you") : t("chat.assistant")}
            </span>
            <pre className="ai-pre">{turn.content || (turn.pending ? "…" : "")}</pre>
          </div>
        ))}
      </div>

      {incomplete && (
        <div className="ai-retry-row">
          <p className="ai-muted mb-1">{t("chat.incomplete")}</p>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => lastPayload && ask(lastPayload)}
          >
            {t("chat.retry")}
          </button>
        </div>
      )}

      <form
        className="ai-chat-form"
        onSubmit={(e) => {
          e.preventDefault();
          ask();
        }}
      >
        <input
          type="text"
          className="form-control"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("chat.placeholder")}
          maxLength={2000}
          disabled={streaming}
        />
        <button
          type="submit"
          className="btn btn-main-color"
          disabled={streaming || !message.trim()}
        >
          {t("chat.send")}
        </button>
      </form>
    </div>
  );
};

export default LessonChatPanel;
